// contentSource — the single, shared definition of where an operator-facing
// string gets its content. Every string-bearing block (text, action label,
// stat label, input label/placeholder, form-submit label, table headers, …)
// resolves its visible text through resolveContentSource() so the behaviour is
// identical everywhere and adding a new source is a one-line change here.
//
// Layering note: "dvar" is resolved SERVER-SIDE — by the time the slot mapping
// reaches React, a dvar-sourced string already has its resolved text sitting in
// the literal prop (blank if the DVar was empty — intentional, no fallback). So
// from React's point of view "literal" and "dvar" are the same: render the
// literal prop verbatim. The "from-*" sources are resolved LIVE here from the
// host's current series / selection.

import type { BlockHostContext } from "./types";

/** Where a string's content comes from. Extensible: add a case here + a row in
 *  CONTENT_SOURCES + a branch in resolveContentSource and every block + the
 *  editor dropdown pick it up. */
export type ContentSource =
  | "literal"
  | "dvar"
  | "from-active-series-label"
  | "from-active-series-description"
  | "from-selected-product-name";

/** The ordered option list shared by the React resolver and (mirrored) by the
 *  editor's content-source dropdown. `label` is the operator-facing option
 *  text; `live` flags sources resolved at render time from host state (vs.
 *  literal/dvar which read the already-resolved literal prop). */
export const CONTENT_SOURCES: { value: ContentSource; label: string; live: boolean }[] = [
  { value: "literal",                        label: "Literal",                      live: false },
  { value: "dvar",                           label: "Translate (DVar)",             live: false },
  { value: "from-active-series-label",       label: "From active series → label",       live: true },
  { value: "from-active-series-description", label: "From active series → description", live: true },
  { value: "from-selected-product-name",     label: "From selected product → name",     live: true },
];

/** The live values a "from-*" source reads. Pulled off the host once in Slot
 *  and handed to each block so the blocks don't each reach into host internals. */
export interface ContentSourceContext {
  seriesLabel?: string | null;
  seriesDescription?: string | null;
  selectedProductName?: string | null;
}

/** Build the live context from the host — the single place Slot reads these. */
export function contentSourceContext(host: BlockHostContext): ContentSourceContext {
  return {
    seriesLabel: host.activeSeries?.label ?? null,
    seriesDescription: host.activeSeries?.description ?? null,
    selectedProductName: host.selectedProductName ?? null,
  };
}

/** Resolve a string's visible content. `literal`/`dvar` return the literal prop
 *  verbatim (dvar was already resolved into it server-side; an empty dvar is an
 *  intentional blank). The `from-*` sources read the live context and return
 *  null when the value isn't available (so the caller can skip rendering). */
export function resolveContentSource(
  source: ContentSource | string | undefined,
  literal: string | null | undefined,
  ctx: ContentSourceContext,
): string | null {
  switch (source) {
    case "from-active-series-label":       return ctx.seriesLabel ?? null;
    case "from-active-series-description": return ctx.seriesDescription ?? null;
    case "from-selected-product-name":     return ctx.selectedProductName ?? null;
    case "literal":
    case "dvar":
    default:                               return literal ?? null;
  }
}
